import { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { useIncidentHubEvent, useIncidentStreams } from '@ngsos/portal-core/portal';

import { ChatUserType, EmergencySituationInstructions } from '@/signalr';
import type { AttachmentBody, CallerConnectedStatus, ChatMessage, NewChatMessageBody } from '@/signalr';
import { NgButton, PanelInfoWithIcon } from '@/ui';
import { useIncidentRealtime } from '@/features/incident-detail/hub/incidentRealtime';
import { activateChatForIncident, fetchChatMessagesForIncident, sendNewChatMessage } from './chatApi';
import { ChatMessageList } from './ChatMessageList';
import { ChatMessageListLoadingPlaceholder } from './ChatMessageListLoadingPlaceholder';
import { INITIAL_INCIDENT_CHAT_STATE, incidentChatReducer } from './incidentChatState';
import { MessageSendPanel } from './MessageSendPanel';

export type IncidentChatProps = {
  incidentId: string;
  callerConnectedStatus: CallerConnectedStatus;
  incidentHasPhoneNumber: boolean;
  incidentIsReadOnly: boolean;
  hasActiveEedVideo: boolean;
  videoExpanded: boolean;
  onRequestVideoCollapse: () => void;
};

export function IncidentChat({
  incidentId,
  callerConnectedStatus,
  incidentHasPhoneNumber,
  incidentIsReadOnly,
  hasActiveEedVideo,
  videoExpanded,
  onRequestVideoCollapse,
}: IncidentChatProps) {
  const { t } = useTranslation();
  const realtime = useIncidentRealtime();
  const { isWatching } = useIncidentStreams(incidentId);
  const [state, dispatch] = useReducer(incidentChatReducer, INITIAL_INCIDENT_CHAT_STATE);

  const allowActivatingChat = !incidentIsReadOnly && incidentHasPhoneNumber;

  useEffect(() => {
    if (realtime === null || !isWatching) {
      return;
    }

    let cancelled = false;
    dispatch({ type: 'loading-started' });

    void (async () => {
      let messages: ChatMessage[] | null;
      try {
        messages = await fetchChatMessagesForIncident(incidentId);
      } catch (error) {
        console.error(String(error));
        if (!cancelled) {
          dispatch({ type: 'loading-failed' });
        }
        return;
      }
      if (cancelled) {
        return;
      }
      dispatch({ type: 'messages-loaded', messages });

      try {
        const liveState = await realtime.getLiveState();
        if (!cancelled) {
          dispatch({
            type: 'hub-state-loaded',
            callerMessagePreview: liveState.callerMessagePreview,
            isCallerSendingImage: liveState.callerIsSendingImage,
            translateChat: liveState.allowChatTranslations,
          });
        }
      } catch (error) {
        console.log('SignalR hub connection was canceled. ' + String(error));
      }

      if (!cancelled) {
        dispatch({ type: 'loading-finished' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [realtime, incidentId, isWatching]);

  const reloadMessages = useCallback(async () => {
    let messages: ChatMessage[] | null;
    try {
      messages = await fetchChatMessagesForIncident(incidentId);
    } catch (error) {
      console.error(String(error));
      return;
    }
    dispatch({ type: 'messages-loaded', messages });
  }, [incidentId]);

  useIncidentHubEvent(incidentId, 'NewMessageReceived', (message) => {
    dispatch({ type: 'new-message-received', message });
    onRequestVideoCollapse();
  });
  useIncidentHubEvent(incidentId, 'CallerChatTextMessagePreviewReceived', (messagePreview) => {
    dispatch({ type: 'caller-text-preview-received', messagePreview });
    onRequestVideoCollapse();
  });
  useIncidentHubEvent(incidentId, 'CallerChatImageMessagePreviewReceived', (isSendingImage) => {
    dispatch({ type: 'caller-image-preview-received', isSendingImage });
  });
  useIncidentHubEvent(incidentId, 'ChatTranslationsAllowedChanged', (translateChat) => {
    dispatch({ type: 'translations-allowed-changed', translateChat });
  });
  useIncidentHubEvent(incidentId, 'ChatActivated', () => {
    void reloadMessages();
  });

  const sendMessage = async (message: string) => {
    const messageBody: NewChatMessageBody = {
      message,
      emergencySituationInstructions: EmergencySituationInstructions.None,
      attachment: null,
      senderType: ChatUserType.Dispatcher,
    };
    await sendNewChatMessage(incidentId, messageBody);
  };

  const sendImageMessage = async (attachment: AttachmentBody) => {
    dispatch({ type: 'send-image-started' });

    try {
      await realtime?.sendDispatcherImagePreview(true);
      const messageBody: NewChatMessageBody = {
        message: null,
        emergencySituationInstructions: EmergencySituationInstructions.None,
        attachment,
        senderType: ChatUserType.Dispatcher,
      };
      await sendNewChatMessage(incidentId, messageBody);
      await realtime?.sendDispatcherImagePreview(false);
    } finally {
      dispatch({ type: 'send-image-finished' });
    }
  };

  const sendMessagePreview = async (message: string) => {
    await realtime?.sendDispatcherTextPreview(message);
  };

  const translateChatChanged = async (translateChat: boolean) => {
    dispatch({ type: 'translations-allowed-changed', translateChat });
    await realtime?.setChatTranslationsAllowed(translateChat);
  };

  const activateChat = async () => {
    await activateChatForIncident(incidentId);
  };

  const activateChatWithEedConfirmation = async () => {
    const confirmed = window.confirm(t('Chat_ActivateChatEedVideoWarning'));
    if (confirmed) {
      await activateChat();
    }
  };

  const sendPanel = (
    <MessageSendPanel
      incidentId={incidentId}
      onMessageSend={sendMessage}
      onMessagePreviewTriggered={sendMessagePreview}
      onMessageImageSend={sendImageMessage}
      callerChatConnectionStatus={callerConnectedStatus}
      isChatLoading={state.isChatLoading}
      incidentIsReadOnly={incidentIsReadOnly}
      translateChat={state.translateChat}
      onTranslateChatChanged={translateChatChanged}
      isSendingImage={state.isSendingImage}
    />
  );

  return (
    <div className="incident-chat">
      {state.isChatLoading ? (
        <ChatMessageListLoadingPlaceholder />
      ) : state.loadFailed ? (
        <PanelInfoWithIcon icon="fal fa-warning" text={t('Loading_GeneralLoadingFailedMessage')}>
          <NgButton
            icon="fal fa-rotate-right"
            text={t('Button_TryAgain')}
            size="small"
            onClick={() => void reloadMessages()}
          />
        </PanelInfoWithIcon>
      ) : state.messages === null ? (
        hasActiveEedVideo || videoExpanded ? (
          allowActivatingChat && (
            <NgButton
              icon="fal fa-message-plus"
              text={t('Chat_ActivateChat')}
              size="small"
              onClick={() => void activateChatWithEedConfirmation()}
            />
          )
        ) : (
          <PanelInfoWithIcon icon="fal fa-message-slash" text={t('Chat_ChatIsInactive')}>
            {allowActivatingChat && (
              <NgButton
                icon="fal fa-message-plus"
                text={t('Chat_ActivateChat')}
                size="small"
                onClick={() => void activateChat()}
              />
            )}
          </PanelInfoWithIcon>
        )
      ) : videoExpanded ? (
        <>
          <div className="incident-chat__collapsed">
            <NgButton
              icon="fal fa-message"
              text={t('Chat_ShowChat')}
              size="small"
              onClick={onRequestVideoCollapse}
            />
          </div>
          <div className="incident-chat__full">
            <div className="incident-chat__top-part">
              <ChatMessageList
                messages={state.messages}
                callerMessagePreview={state.callerMessagePreview}
                isCallerSendingImage={state.isCallerSendingImage}
                translateChat={state.translateChat}
              />
            </div>
            <div className="incident-chat__bottom-part">{sendPanel}</div>
          </div>
        </>
      ) : (
        <>
          <div className="incident-chat__top-part">
            <ChatMessageList
              messages={state.messages}
              callerMessagePreview={state.callerMessagePreview}
              isCallerSendingImage={state.isCallerSendingImage}
              translateChat={state.translateChat}
            />
          </div>

          <div className="incident-chat__bottom-part">{sendPanel}</div>
        </>
      )}
    </div>
  );
}
